import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, CreditCard, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import OTPModal from '../components/OTPModal';

const UserLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [loginResponse, setLoginResponse] = useState(null);
  const [formData, setFormData] = useState({
    accountNumber: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await userAPI.login(formData.accountNumber, formData.password);
      
      // Store response and user email for OTP verification
      setLoginResponse(response);
      setUserEmail(response.user.email);
      
      // Generate and send OTP
      await userAPI.generateOTP(response.user.email, 'login');
      
      toast({
        title: "Verification Required",
        description: "Please check your email for the OTP code",
      });
      
      setShowOTP(true);
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "Invalid credentials",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (otpCode) => {
    setOtpLoading(true);
    
    try {
      // Verify OTP
      await userAPI.verifyOTP(userEmail, otpCode, 'login');
      
      // If OTP is valid, complete login
      login(loginResponse.token, loginResponse.user);
      
      toast({
        title: "Login Successful",
        description: `Welcome back, ${loginResponse.user.firstName}!`,
      });
      
      navigate('/user/dashboard');
    } catch (error) {
      setOtpLoading(false);
      throw error; // Let OTPModal handle the error display
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      {/* Back to Homepage Button */}
      <Button
        onClick={() => navigate('/')}
        variant="outline"
        className="fixed top-4 left-4 z-50"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Homepage
      </Button>

      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-4 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center mx-auto">
            <User className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">Customer Login</CardTitle>
          <p className="text-gray-600">Access your Morgan Guaranty Trust account</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CreditCard className="w-4 h-4 inline mr-2" />
                Account Number
              </label>
              <Input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                placeholder="Enter 10-digit account number"
                required
                maxLength={10}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 space-y-3 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/user/register" className="text-blue-600 hover:text-blue-700 font-medium">
                Register Now
              </Link>
            </p>
            <button
              onClick={() => navigate('/admin/login')}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Admin Login →
            </button>
          </div>
        </CardContent>
      </Card>

      {/* OTP Modal */}
      <OTPModal
        isOpen={showOTP}
        onClose={() => setShowOTP(false)}
        onVerify={handleOTPVerify}
        email={userEmail}
        purpose="login"
        isLoading={otpLoading}
      />
    </div>
  );
};

export default UserLogin;
